import React from 'react';
import { ChevronDown } from 'lucide-react';

interface Props {
  codes?: string[];
  maxVisible?: number;
  /** Stato di espansione controllato dal genitore (la lista completa si
   * espande in una sezione full-width sotto la riga, stesso pattern della
   * compatibilità — vedi ProductSearchPage.tsx). */
  expanded?: boolean;
  onToggle?: () => void;
}

/**
 * Colonna codici OEM nella lista articoli (colonna 3 del layout a 4
 * colonne): max 3 codici su una sola colonna verticale (per restare
 * dentro l'altezza fissa della card), con un bottone "+" identico per
 * stile/comportamento al toggle compatibilità quando ce ne sono altri —
 * click espande la sezione dedicata sotto la riga (gestita dal genitore).
 */
export function OemCodesColumn({ codes, maxVisible = 3, expanded = false, onToggle }: Props) {
  if (!codes || codes.length === 0) return null;

  const visible = codes.slice(0, maxVisible);
  const hidden = codes.slice(maxVisible);
  const hiddenCount = hidden.length;
  const chipClass = "text-xs font-mono font-semibold px-1.5 py-0.5 rounded leading-tight whitespace-nowrap text-slate-700";

  return (
    <div className="flex items-start gap-1.5 min-w-0" data-testid="product-oem-column">
      <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wide flex-shrink-0 mt-0.5">OEM</span>
      <div className="flex flex-col gap-1 min-w-0">
        {visible.map(code => (
          <span key={code} className={chipClass}>
            {code}
          </span>
        ))}
        {hiddenCount > 0 && (
          <div
            className="flex flex-col gap-1 overflow-hidden transition-[max-height] duration-300 ease-in-out"
            style={{ maxHeight: expanded ? `${hiddenCount * 26 + 4}px` : '0px' }}
            data-testid="product-oem-hidden-list"
          >
            {hidden.map(code => (
              <span key={code} className={chipClass}>
                {code}
              </span>
            ))}
          </div>
        )}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle?.(); }}
            title={expanded ? 'Mostra meno' : `Mostra tutti i codici OEM (+${hiddenCount})`}
            data-testid="product-oem-expand"
            className="flex-shrink-0 self-center w-5 h-5 rounded-full border border-[hsl(214,58%,41%)]/30 text-[hsl(214,58%,45%)] hover:bg-[hsl(214,58%,41%)]/10 transition-colors flex items-center justify-center"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
    </div>
  );
}
